import { Link } from 'react-router-dom';
import { COACHING_ASTRO_DISCLAIMER, type CoachingAccessKey } from '@mlm/shared/coaching';
import {
  Block,
  ErrorNote,
  Eyebrow,
  Loading,
  Row,
  Screen,
  Stat,
  TwoCol,
} from '../components/primitives';
import { AlignmentMeters } from '../components/alignment-meters';
import { ChartPanel } from '../components/panels/chart';
import { GuidancePanel } from '../components/panels/guidance';
import {
  useChart,
  useFeature,
  useGuidance,
  useProfile,
  useToday,
  useFeatureLabel,
  useFeatureLocked,
} from '../lib/queries';
import { ProLock } from '../components/pro-lock';
import { useIsDesktop } from '../lib/use-is-desktop';
import { dayLine } from '../lib/home';

/**
 * SC-26: the alignment score and the way into the Vedic guidance and the chart.
 * Desktop shows today's guidance and the chart side by side.
 */
export function AlignPage() {
  const today = useToday();
  const desktop = useIsDesktop();
  if (today.isPending) return <Loading />;
  if (today.isError) return <ErrorNote error={today.error} retry={() => void today.refetch()} />;
  const a = today.data.alignment;

  if (!today.data.profile.hasBirthDetails || !a) {
    return (
      <Screen eyebrow="Align" title="Align">
        <Block
          title="Add your birth details"
          action={
            <Link
              to="/settings"
              className="inline-flex min-h-11 items-center bg-gold px-6 font-mono text-[0.68rem] uppercase tracking-[0.14em] text-ink hover:bg-[#c29d4c]"
            >
              Add birth details
            </Link>
          }
        >
          Your birth date, time and place let Miracles Protocol read your chart and write guidance
          for each day.
        </Block>
        <AlignReadings hasBirthDetails={false} />
        <AlignPractices />
        <AlignLearn />
      </Screen>
    );
  }

  if (desktop) {
    return (
      <Screen
        wide
        eyebrow="Align"
        title="Align"
        aside={
          <Stat eyebrow="Today's alignment score" value={a.overall} label={dayLine(a.overall)} />
        }
      >
        <TwoCol split="1/1" left={<GuidanceColumn />} right={<ChartColumn />} />
        <AlignReadings hasBirthDetails />
        <AlignPractices />
        <AlignLearn />
      </Screen>
    );
  }

  return (
    <Screen eyebrow="Align" title="Align">
      <section className="border-b border-border px-5 py-6">
        <Eyebrow>Today's alignment score</Eyebrow>
        <div className="mt-2 flex items-baseline gap-3">
          <span className="font-display text-6xl font-semibold leading-none text-ink">
            {a.overall}
          </span>
          <span className="eyebrow">{dayLine(a.overall)}</span>
        </div>
        <div className="mt-6 space-y-5">
          <AlignmentMeters alignment={a} />
        </div>
      </section>
      <Row
        title="Today's Vedic guidance"
        meta="Theme, best use of the day, what to watch"
        to="/align/vedic"
      />
      <Row title="Your birth chart" meta="Ascendant, nakshatra, dasha, yogas" to="/align/chart" />
      <AlignReadings hasBirthDetails />
      <AlignPractices />
      <AlignLearn />
      <p className="px-5 py-6 text-xs leading-relaxed text-muted-foreground">
        {COACHING_ASTRO_DISCLAIMER}
      </p>
    </Screen>
  );
}

/**
 * SC-26's practice rows. Each one asks about ITSELF now: the tenant can put
 * breathwork on Free and mantras on Pro, which the single group switch these
 * rows used to share could never express. A practice the tenant switched off
 * is not listed at all — there is nothing to sell, so a lock would be noise.
 */
function AlignPractices() {
  const profile = useProfile();
  const hasBirthDate = Boolean(profile.data?.profile.birthDate);
  const rows: Array<{ key: CoachingAccessKey; title: string; meta: string; to: string }> = [
    {
      key: 'breathwork',
      title: 'Breathwork',
      meta: 'Calm, focus, energy and sleep',
      to: '/align/breathwork',
    },
    {
      key: 'frequencies',
      title: 'Frequencies',
      meta: 'Listening sessions for focus and rest',
      to: '/align/frequencies',
    },
    {
      key: 'biorhythm',
      title: 'Biorhythm',
      meta: hasBirthDate ? 'Your four cycles, colours and stone' : 'Needs your birth date',
      to: '/align/biorhythm',
    },
    {
      key: 'mantras',
      title: 'Mantras',
      meta: 'A phrase to repeat, and a counter',
      to: '/align/mantras',
    },
    {
      key: 'meditation',
      title: 'Guided meditation',
      meta: 'Short sessions, a step at a time',
      to: '/align/meditations',
    },
  ];
  return (
    <section>
      <Eyebrow className="border-b border-border px-5 pb-3 pt-7">Practices</Eyebrow>
      {rows.map((row) => (
        <PracticeRow
          key={row.key}
          feature={row.key}
          title={row.title}
          meta={row.meta}
          to={row.to}
        />
      ))}
    </section>
  );
}

function PracticeRow({
  feature,
  title,
  meta,
  to,
}: {
  feature: CoachingAccessKey;
  title: string;
  meta: string;
  to: string;
}) {
  const state = useFeature(feature);
  const plan = useFeatureLabel(feature);
  if (state === 'off') return null;
  return <Row title={title} meta={state === 'locked' ? `${meta} · ${plan}` : meta} to={to} />;
}

/**
 * Readings that are not shown inline on this screen. Desktop renders the
 * guidance and the chart side by side and never reaches the rows below them,
 * so anything that only lives in a row has to be rendered here too or a
 * desktop member can never get to it.
 */
function AlignReadings({ hasBirthDetails }: { hasBirthDetails: boolean }) {
  return (
    <section>
      <Eyebrow className="border-b border-border px-5 pb-3 pt-7">Readings</Eyebrow>
      {/* Karma and aura are read from the chart, so they wait for it. The palm
          reading needs nothing at all, and hiding it behind birth details the
          member has not given would hide it from them for no reason. */}
      {hasBirthDetails && (
        <>
          <Row
            title="Karma reading"
            meta="The pattern you fall back on, and what it is asking"
            to="/align/karma"
          />
          <Row
            title="Aura reading"
            meta="Five questions, then your colours and centres"
            to="/align/aura"
          />
        </>
      )}
      <Row
        title="Palm reading"
        meta="A photo of your hand, read once and not saved"
        to="/align/palmistry"
      />
    </section>
  );
}

/** The way into the glossary. Shown in every state: it needs no birth details. */
function AlignLearn() {
  return (
    <section>
      <Eyebrow className="border-b border-border px-5 pb-3 pt-7">Learn</Eyebrow>
      <Row
        title="The words in your reading"
        meta="What a dasha, a nakshatra and a yoga are"
        to="/align/learn"
      />
      <PracticeRow
        feature="library"
        title="Library"
        meta="Videos, audio, pictures and guides from the team"
        to="/library"
      />
    </section>
  );
}

function GuidanceColumn() {
  const locked = useFeatureLocked('guidance');
  const guidance = useGuidance();
  if (locked) return <ProLock feature="guidance" />;
  if (guidance.isPending) return <Loading label="Reading today's sky" />;
  if (guidance.isError)
    return <ErrorNote error={guidance.error} retry={() => void guidance.refetch()} />;
  return <GuidancePanel g={guidance.data} heading />;
}

function ChartColumn() {
  const locked = useFeatureLocked('chart');
  const chart = useChart();
  if (locked) return <ProLock feature="chart" />;
  if (chart.isPending) return <Loading label="Casting your chart" />;
  if (chart.isError) return <ErrorNote error={chart.error} retry={() => void chart.refetch()} />;
  return <ChartPanel data={chart.data} heading />;
}
